import { DEFAULT_COLLECTION_ID, type SearchArticleMeta } from "@serophin/nottldr-shared";
import { useBookmarks, useReadArticles } from "@serophin/nottldr-shared/hooks";
import { BookCheck, BookmarkCheck, BookmarkPlus, BookPlus, Flag, FolderCheck, FolderPlus, Share2 } from "lucide-preact";
import { useState } from "preact/hooks";
import { useAuthModal } from "../../contexts/AuthModalContext";
import { useToast } from "../../contexts/ToastContext";
import { useIsSignedIn } from "../../platform/session";
import { FollowButton } from "../author/FollowButton";
import { DropdownItem } from "./Dropdown";

type MenuProps = {
  meta: SearchArticleMeta;
  closeMenu: () => void;
  openModal: (modal: "collections" | "share" | "report") => void;
};

const testIds = (meta: SearchArticleMeta, click: string) => ({ "data-test-click": click, "data-test-article": meta.id, "data-test-author": meta.authorAlias });

/** Runs `action` while `busy` is true; a failure is a toast. Signed-out visitors are asked to sign in instead. */
const useGatedAction = (title: string, message: string, failure: string) => {
  const signedIn = useIsSignedIn();
  const { showAuthModal } = useAuthModal();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<unknown>) => {
    if (!signedIn) {
      showAuthModal(title, message);
      return;
    }
    setBusy(true);
    try {
      await action();
    } catch {
      toast.error(failure);
    } finally {
      setBusy(false);
    }
  };

  return { run, busy };
};

const ReadLaterItem = ({ meta }: { meta: SearchArticleMeta }) => {
  const { collectionIdsOf, add, remove } = useBookmarks();
  const { run, busy } = useGatedAction(
    "Sign in to save articles",
    "Create an account or sign in to save articles to Read Later",
    "Failed to update Read Later. Please try again.",
  );
  const saved = collectionIdsOf(meta.authorAlias, meta.id).includes(DEFAULT_COLLECTION_ID);
  const toggle = () => run(() => (saved ? remove(DEFAULT_COLLECTION_ID, meta.authorAlias, meta.id) : add(DEFAULT_COLLECTION_ID, meta.authorAlias, meta.id)));

  return (
    <DropdownItem
      icon={saved ? BookmarkCheck : BookmarkPlus}
      isActive={saved}
      label={busy ? (saved ? "Removing..." : "Adding...") : saved ? "Remove from Read Later" : "Add to Read Later"}
      onClick={toggle}
      {...testIds(meta, "menu-add-to-read-later")}
    />
  );
};

const CollectionsItem = ({ meta, closeMenu, openModal }: MenuProps) => {
  const { collectionIdsOf } = useBookmarks();
  const signedIn = useIsSignedIn();
  const { showAuthModal } = useAuthModal();
  const held = collectionIdsOf(meta.authorAlias, meta.id).length > 0;

  const open = () => {
    if (signedIn) {
      openModal("collections");
    } else {
      showAuthModal("Sign in to save articles", "Create an account or sign in to save articles to your collections");
    }
    closeMenu();
  };

  return (
    <DropdownItem
      icon={held ? FolderCheck : FolderPlus}
      isActive={held}
      label="Add to Collection"
      onClick={open}
      {...testIds(meta, "menu-add-to-collection")}
    />
  );
};

const MarkReadItem = ({ meta }: { meta: SearchArticleMeta }) => {
  const { isRead, markRead, markUnread, isLoading } = useReadArticles();
  const { run, busy } = useGatedAction(
    "Sign in to mark articles as read",
    "Create an account or sign in to track your reading progress",
    "Failed to update read status. Please try again.",
  );
  const read = isRead(meta.authorAlias, meta.id);
  const toggle = async () => {
    if (read) {
      await markUnread(meta.authorAlias, meta.id);
    } else {
      await markRead(meta.authorAlias, meta.id);
    }
  };
  const label = isLoading ? "Loading..." : busy ? (read ? "Marking as Unread..." : "Marking as Read...") : read ? "Mark as Unread" : "Mark as Read";

  return (
    <DropdownItem
      icon={read ? BookCheck : BookPlus}
      isActive={read}
      label={label}
      onClick={() => run(toggle)}
      {...testIds(meta, "menu-mark-as-read")}
      data-test-action={read ? "unread" : "read"}
    />
  );
};

export const ArticleMenuItems = ({ meta, closeMenu, openModal }: MenuProps) => {
  const signedIn = useIsSignedIn();
  const { showAuthModal } = useAuthModal();
  const choose = (modal: "share" | "report") => () => {
    if (modal === "report" && !signedIn) {
      showAuthModal("Sign in to report", "Create an account or sign in to report articles");
    } else {
      openModal(modal);
    }
    closeMenu();
  };

  return (
    <>
      <ReadLaterItem meta={meta} />
      <CollectionsItem meta={meta} closeMenu={closeMenu} openModal={openModal} />
      <MarkReadItem meta={meta} />
      <FollowButton alias={meta.authorAlias} display="dropdown-item" />
      <div class="h-px bg-gray-200 dark:bg-neutral-700" />
      <DropdownItem icon={Share2} label="Share" onClick={choose("share")} {...testIds(meta, "menu-share-article")} />
      <DropdownItem icon={Flag} label="Report" onClick={choose("report")} {...testIds(meta, "menu-report-article")} />
    </>
  );
};
